import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import { selectCapitalStructure } from '@app/core/store/capital-structure-slice-selectors';
import {
  getRequiredValidator,
  greaterDateThanValidator,
  lesserOrEqualDateThanValidator,
  maxLengthValidator,
  uniqueEventNarrative,
} from '@app/core/validations/hook-forms/validators';
import FormCheckbox from '@app/shared/components/form-controls/form-checkbox/FormCheckbox';
import FormDatePicker from '@app/shared/components/form-controls/form-date-picker/FormDatePicker';
import { textFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import FormField from '@app/shared/components/form-controls/form-field/FormField';
import { CalcMethod, EventKey } from '@app/shared/models/contracts/enums/shared-enums';
import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

const NewEventFormElements: FC = (): JSX.Element => {
  const { l } = useLocale();
  const project = useAppSelector((state) => state.project.projectDraft);
  const { maxRefinancingEventNameLength: maxEventNameLength, maxCapitalStructureYears } =
    useAppSelector((state) => state.uiValues);
  const options = project.pwermInput.cases.map((cases) => ({
    viewValue: cases.narrative,
    value: cases.caseId,
  }));
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const isOpmOnly = project.details.calcMethod === CalcMethod.OPM;
  const existingEvents = isOpmOnly
    ? Object.fromEntries(
        capitalStructure.eventSets[EventKey.OpmOnly]?.events.map((eventId) => [
          eventId,
          capitalStructure.events[eventId],
        ]) ?? []
      )
    : Object.fromEntries(
        Object.entries(capitalStructure.events).filter(
          ([eventId, _]) => !capitalStructure.eventSets[EventKey.OpmOnly]?.events.includes(eventId)
        )
      );
  const valuationDate = new Date(project.valuationDate);
  const minAllowedEventDate =
    new Date(project.investmentDate).addDays(1).toISODateString() ?? undefined;
  const maxAllowedEventDate = project.yearEndDate
    ? new Date(project.yearEndDate).addYears(maxCapitalStructureYears).toISODateString()
    : undefined;
  const { watch, setValue, trigger } = useFormContext();
  const eventDate = watch('eventDate');
  const isEventDateOnOrBeforeValuationDate = eventDate && new Date(eventDate) <= valuationDate;
  const isSingleCase = !isOpmOnly && project.pwermInput.cases.length === 1;

  useEffect(() => {
    if (isEventDateOnOrBeforeValuationDate || isSingleCase) {
      setValue(
        'selectedCases',
        project.pwermInput.cases.map((cases) => cases.caseId)
      );
    } else {
      setValue('selectedCases', []);
    }
  }, [isEventDateOnOrBeforeValuationDate, isSingleCase, setValue, project.pwermInput.cases]);

  useEffect(() => {
    trigger('eventDate');
  }, [trigger, maxCapitalStructureYears, project.yearEndDate]);

  return (
    <>
      <FormField
        name="narrative"
        label={l('_EventNarrativeLowercase')}
        placeholder={l('_Narrative')}
        {...textFieldFormattingProps}
        rules={{
          ...getRequiredValidator(),
          ...uniqueEventNarrative(existingEvents),
          ...maxLengthValidator(maxEventNameLength),
        }}
      />
      <FormDatePicker
        data-testid="eventDate"
        name="eventDate"
        label={l('_EventDate')}
        minDate={minAllowedEventDate}
        maxDate={maxAllowedEventDate}
        rules={{
          ...getRequiredValidator(),
          validate: {
            greaterDateThanInvestmentDate: greaterDateThanValidator(
              project.investmentDate,
              l('_EventDateMustBeGreaterThanInvestmentDate')
            ).validate,
            lessThanMaxCapitalStructureDate: lesserOrEqualDateThanValidator(
              maxAllowedEventDate ?? null
            ).validate,
          },
        }}
      />
      {!isOpmOnly && (
        <FormCheckbox
          name="selectedCases"
          label={isEventDateOnOrBeforeValuationDate ? l('_AllCasesSelected') : l('_SelectCases')}
          rules={{
            ...(!isEventDateOnOrBeforeValuationDate && getRequiredValidator()),
          }}
          options={options}
          disabled={isEventDateOnOrBeforeValuationDate}
        />
      )}
    </>
  );
};

export default NewEventFormElements;
